import { Trans, useTranslation } from "react-i18next";

import { Typography, Spacer } from "@aviary";
import { useGlobalConfig } from "@clinic/shared/hooks/useGlobalConfig/useGlobalConfig";
import { l } from "@emerson/locales/i18n";
import { FlipperPriceBlur } from "@shared/components/FlipperPriceBlur/FlipperPriceBlur";
import { FreeShippingMeter } from "@shared/components/FreeShippingMeter/FreeShippingMeter";
import { getCashValue } from "@shared/utils/getCashValue/getCashValue";

import * as styles from "./FreeShippingBar.styles";

interface Props {
  numberOfCartItems?: number;
  subtotal?: number;
}

const FreeShippingBar = ({ numberOfCartItems = 0, subtotal = 0 }: Props) => {
  const { t } = useTranslation();
  const { freeShippingThreshold } = useGlobalConfig();

  const amountLeft = freeShippingThreshold - subtotal;
  const thresholdReached = amountLeft <= 0;

  const freeShippingStateText = () => {
    if (numberOfCartItems === 0) return t(l.cart.ShippingThresholdHelpText);
    if (amountLeft > 0) {
      return (
        <Trans
          i18nKey={l.cart.AwayFromFreeShippingText}
          values={{ amountLeft: getCashValue(amountLeft) }}
          components={{ blur: <FlipperPriceBlur /> }}
        />
      );
    }

    return t(l.cart.FreeShippingThresholdReached, {
      threshold: getCashValue(freeShippingThreshold),
    });
  };

  const freeShippingHelpText = thresholdReached ? (
    t(l.cart.CongratsYouHaveFreeShipping)
  ) : (
    <Trans
      i18nKey={l.cart.FreeShippingThreshold}
      values={{ freeShippingThreshold: getCashValue(freeShippingThreshold) }}
    />
  );

  const renderSubText = () =>
    thresholdReached && (
      <>
        <Spacer height="quarter" />
        <Typography type="footnote" isMarginless css={styles.centeredText}>
          {t(l.cart.DiscountApplied)}
        </Typography>
      </>
    );

  return (
    <FreeShippingMeter
      freeShippingHelpText={freeShippingHelpText}
      subtotal={subtotal}
      freeShippingThreshold={freeShippingThreshold}
    >
      <div>
        <Typography type="footnote" isMarginless={thresholdReached} css={styles.centeredText}>
          {freeShippingStateText()}
        </Typography>
        {renderSubText()}
      </div>
    </FreeShippingMeter>
  );
};

export { FreeShippingBar };
